import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Radar, Search, Server, ShieldAlert, AlertOctagon, Globe, Lock, Unlock,
  Cpu, Camera, KeyRound, Sparkles, Info, Loader2, ExternalLink,
} from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/osint`;

const BAND_COLORS = {
  critical:    { bg: "bg-[#FF1744]/12", border: "border-[#FF1744]/40", text: "text-[#FF1744]", label_it: "Critico",    label_en: "Critical" },
  high_risk:   { bg: "bg-[#FF8A00]/12", border: "border-[#FF8A00]/40", text: "text-[#FF8A00]", label_it: "Alto rischio", label_en: "High risk" },
  medium_risk: { bg: "bg-[#FFB800]/12", border: "border-[#FFB800]/40", text: "text-[#FFB800]", label_it: "Rischio medio", label_en: "Medium risk" },
  healthy:     { bg: "bg-[#00E676]/12", border: "border-[#00E676]/40", text: "text-[#00E676]", label_it: "Sano",       label_en: "Healthy" },
};

const SEVERITY_COLORS = {
  critical: "text-[#FF1744] bg-[#FF1744]/12 border-[#FF1744]/30",
  high:     "text-[#FF8A00] bg-[#FF8A00]/12 border-[#FF8A00]/30",
  medium:   "text-[#FFB800] bg-[#FFB800]/12 border-[#FFB800]/30",
  low:      "text-[#00E676] bg-[#00E676]/12 border-[#00E676]/30",
  unknown:  "text-white bg-white/[0.04] border-white/[0.08]",
};

export default function CyberExposure() {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ mode: "demo", has_shodan_key: false });
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/status`, { withCredentials: true })
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, []);

  const runSearch = useCallback(async (q) => {
    const query = (q || "").trim();
    if (!query) return;
    setLoading(true);
    setError("");
    setHost(null);
    try {
      const r = await axios.get(`${API}/search`, { params: { q: query }, withCredentials: true });
      setHost(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  }, []);

  // Deep-link: /cyber-exposure?q=TechVision
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const q = p.get("q");
    if (q) {
      setQuery(q);
      runSearch(q);
      navigate("/cyber-exposure", { replace: true });
    }
  }, [location.search, runSearch, navigate]);

  return (
    <div className="app-page-dark min-h-screen bg-[#050505] text-white pb-20" data-testid="cyber-exposure-page">
      {/* Header */}
      <div className="border-b border-white/[0.04] bg-[#0A0C10] sticky top-0 z-20 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Radar className="w-5 h-5 text-[#00E5FF]" />
                <h1 className="text-xl font-bold tracking-tight text-white">Cyber Exposure</h1>
                {status.mode === "demo" && (
                  <span className="text-[9px] tracking-widest uppercase font-bold px-2 py-0.5 bg-[#FFB800]/12 text-[#FFB800] border border-[#FFB800]/30" style={{ borderRadius: "2px" }} data-testid="cyber-demo-badge">
                    Demo Mode
                  </span>
                )}
              </div>
              <p className="text-[11px] text-white max-w-2xl">
                {lang === "it"
                  ? "Profilo di esposizione digitale delle aziende target. Asset IT esposti, porte, servizi, vulnerabilità note e certificati SSL — solo dati pubblicamente indicizzati."
                  : "Digital exposure profile of target companies. Exposed IT assets, ports, services, known vulnerabilities and SSL certificates — publicly indexed data only."}
              </p>
            </div>
          </div>

          {/* Search bar */}
          <form
            className="mt-4"
            onSubmit={(e) => { e.preventDefault(); runSearch(query); }}
          >
            <div className="flex items-center gap-2 bg-[#050505] border border-white/[0.08] px-3 py-2" style={{ borderRadius: "2px" }}>
              <Search className="w-4 h-4 text-white" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={lang === "it" ? "Cerca dominio, IP o organizzazione (es. techvision.com, 8.8.8.8, TechVision)…" : "Search domain, IP or organization (e.g. techvision.com, 8.8.8.8, TechVision)…"}
                className="flex-1 bg-transparent text-sm text-white placeholder-[#94A3B8] outline-none"
                data-testid="cyber-search-input"
              />
              <button
                type="submit"
                disabled={loading || !query}
                className="bg-[#00E5FF] text-black text-[10px] tracking-widest uppercase font-bold px-3 py-1.5 hover:bg-white disabled:opacity-40"
                style={{ borderRadius: "2px" }}
                data-testid="cyber-search-submit"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (lang === "it" ? "Scansiona" : "Scan")}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {loading && (
          <div className="text-center py-16 text-white text-sm" data-testid="cyber-loading">
            <Sparkles className="w-6 h-6 mx-auto mb-3 text-[#00E5FF] animate-pulse" />
            {lang === "it" ? "Recupero indicizzazioni Shodan e analisi in corso…" : "Fetching Shodan indexes and analysing…"}
          </div>
        )}
        {error && !loading && (
          <div className="bg-[#FF1744]/10 border border-[#FF1744]/40 p-3 text-[#FF1744] text-sm" data-testid="cyber-error">{error}</div>
        )}
        {!loading && !error && !host && (
          <div className="text-center py-24 text-white text-sm max-w-lg mx-auto" data-testid="cyber-empty">
            <Radar className="w-10 h-10 mx-auto mb-3 text-white" />
            <p>{lang === "it" ? "Inizia inserendo un dominio, un IP o il nome di un'azienda per valutarne l'esposizione digitale." : "Enter a domain, IP or company name to assess digital exposure."}</p>
          </div>
        )}
        {host && !loading && <HostView host={host} />}

        {/* Disclaimer */}
        <div className="mt-8 bg-[#0D0F12] border border-white/[0.04] p-3 flex items-start gap-2 text-[10px] text-white" style={{ borderRadius: "2px" }} data-testid="cyber-disclaimer">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <p>{host?.disclaimer || (lang === "it" ? "I dati mostrati sono informazioni pubblicamente indicizzate (Shodan). Da usare per risk assessment, mai per attività intrusive." : "Data shown is publicly indexed information (Shodan). For risk assessment only, never intrusive use.")}</p>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Host detail view
   ──────────────────────────────────────────────────────────── */
function HostView({ host }) {
  const { lang } = useLanguage();
  const score = host.cyber_exposure_score;
  const band = BAND_COLORS[score.band] || BAND_COLORS.healthy;
  const cert = host.certificate || {};

  return (
    <div className="space-y-4">
      {/* Header card: identity + score */}
      <div className={`bg-[#0D0F12] border ${band.border} p-5`} style={{ borderRadius: "2px" }} data-testid="cyber-host-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Server className="w-4 h-4 text-[#00E5FF]" />
              <p className="text-[10px] tracking-widest uppercase text-white">Host</p>
              {host.source === "demo" && (
                <span className="text-[9px] tracking-widest uppercase text-[#FFB800]">· Demo</span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-white tabular-nums" data-testid="cyber-host-ip">{host.ip}</h2>
            <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-white">
              {host.organization && <span><b className="text-white">{host.organization}</b></span>}
              {host.asn && <span>ASN: <span className="text-white tabular-nums">{host.asn}</span></span>}
              {host.geo?.country_name && (
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {host.geo.city}, {host.geo.country_name} ({host.geo.country_code})
                </span>
              )}
            </div>
            {host.hostnames && host.hostnames.length > 0 && (
              <div className="flex items-center gap-1 mt-2 flex-wrap">
                {host.hostnames.map(h => (
                  <span key={h} className="text-[10px] tracking-widest px-1.5 py-0.5 bg-white/[0.04] text-[#B39BFF] font-mono" style={{ borderRadius: "2px" }}>{h}</span>
                ))}
              </div>
            )}
          </div>
          {/* Score */}
          <div className="text-right">
            <p className="text-[10px] tracking-widest uppercase text-white mb-1">
              {lang === "it" ? "Cyber Exposure Score" : "Cyber Exposure Score"}
            </p>
            <p className="text-4xl font-bold text-white tabular-nums" data-testid="cyber-score-value">{score.score}<span className="text-lg text-white">/100</span></p>
            <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 mt-2 inline-block ${band.bg} ${band.text}`} style={{ borderRadius: "2px" }} data-testid="cyber-score-band">
              {lang === "it" ? band.label_it : band.label_en}
            </span>
          </div>
        </div>

        {/* Score breakdown */}
        <div className="mt-4 pt-3 border-t border-white/[0.04]">
          <p className="text-[9px] tracking-widest uppercase text-white mb-2">{lang === "it" ? "Formula" : "Formula"}: <span className="text-white font-mono">{score.formula}</span></p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <BreakdownChip label={lang === "it" ? "Porte aperte" : "Open ports"} value={score.breakdown.open_ports.count} penalty={score.breakdown.open_ports.penalty} />
            <BreakdownChip label="CVE critiche" value={score.breakdown.cves_critical.count} penalty={score.breakdown.cves_critical.penalty} />
            <BreakdownChip label="CVE high" value={score.breakdown.cves_high.count} penalty={score.breakdown.cves_high.penalty} />
            <BreakdownChip label={lang === "it" ? "Cert scaduto" : "Cert expired"} value={score.breakdown.cert_expired.value ? "sì" : "no"} penalty={score.breakdown.cert_expired.penalty} />
            <BreakdownChip label="CCTV/IoT" value={score.breakdown.cctv_exposed.count} penalty={score.breakdown.cctv_exposed.penalty} />
          </div>
        </div>
      </div>

      {/* LLM narrative */}
      {host.llm_narrative && (
        <div className="bg-[#7C3AED]/6 border border-[#7C3AED]/25 p-4" style={{ borderRadius: "2px" }} data-testid="cyber-narrative">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#B39BFF]" />
            <p className="text-[10px] tracking-widest uppercase font-bold text-[#B39BFF]">
              {lang === "it" ? "Sintesi cyber" : "Cyber summary"}
            </p>
          </div>
          <p className="text-sm text-white leading-relaxed">{host.llm_narrative}</p>
        </div>
      )}

      {/* Palantir-style asset map */}
      {host.geo?.lat != null && host.geo?.lon != null && (
        <AssetMap host={host} />
      )}

      {/* Services */}
      <SectionCard icon={Cpu} color="#00E5FF" title={lang === "it" ? "Servizi rilevati" : "Detected services"} count={host.services.length} testid="cyber-services-card">
        {host.services.length === 0 ? (
          <p className="text-[11px] text-white italic px-3 py-4">—</p>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {host.services.map((s, i) => (
              <li key={i} className="px-3 py-2" data-testid={`cyber-service-${s.port}`}>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono tabular-nums text-[#00E5FF] w-14">{s.port}/{s.transport}</span>
                  <span className="text-xs font-bold text-white">{s.product || "—"}</span>
                  {s.version && <span className="text-[10px] text-white">{s.version}</span>}
                </div>
                {s.banner && (
                  <p className="text-[10px] text-white mt-1 font-mono line-clamp-2">{s.banner}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* CVE */}
      <SectionCard icon={ShieldAlert} color="#FF1744" title={lang === "it" ? "Vulnerabilità note (CVE)" : "Known vulnerabilities (CVE)"} count={host.cves.length} testid="cyber-cve-card">
        {host.cves.length === 0 ? (
          <p className="text-[11px] text-white italic px-3 py-4">
            {lang === "it" ? "Nessuna CVE nota associata a questo host." : "No known CVE associated with this host."}
          </p>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {host.cves.map((c) => (
              <li key={c.id} className="px-3 py-2 flex items-start gap-3" data-testid={`cyber-cve-${c.id}`}>
                <span className={`text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 border ${SEVERITY_COLORS[c.severity] || SEVERITY_COLORS.unknown}`} style={{ borderRadius: "2px" }}>
                  {c.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <a href={`https://nvd.nist.gov/vuln/detail/${c.id}`} target="_blank" rel="noopener noreferrer"
                    className="text-xs font-mono font-bold text-[#00E5FF] hover:text-white inline-flex items-center gap-1">
                    {c.id} <ExternalLink className="w-3 h-3" />
                  </a>
                  <p className="text-[11px] text-white mt-0.5">{c.summary}</p>
                </div>
                <span className="text-[10px] tabular-nums text-white font-bold w-12 text-right">CVSS {c.cvss}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* SSL Certificate */}
      <SectionCard icon={cert.expired ? Unlock : Lock} color={cert.expired ? "#FF1744" : "#00E676"} title={lang === "it" ? "Certificato SSL/TLS" : "SSL/TLS certificate"} count="" testid="cyber-cert-card">
        {!cert.subject_cn ? (
          <p className="text-[11px] text-white italic px-3 py-4">—</p>
        ) : (
          <div className="px-3 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
            <div>
              <p className="text-[9px] tracking-widest uppercase text-white">Subject CN</p>
              <p className="text-white font-mono truncate" title={cert.subject_cn}>{cert.subject_cn}</p>
            </div>
            <div>
              <p className="text-[9px] tracking-widest uppercase text-white">Issuer</p>
              <p className="text-white truncate" title={cert.issuer}>{cert.issuer}</p>
            </div>
            <div>
              <p className="text-[9px] tracking-widest uppercase text-white">{lang === "it" ? "Scadenza" : "Expires"}</p>
              <p className={`font-mono ${cert.expired ? "text-[#FF1744]" : "text-white"}`}>{(cert.expires || "").slice(0, 10)}</p>
            </div>
            <div>
              <p className="text-[9px] tracking-widest uppercase text-white">{lang === "it" ? "Giorni residui" : "Days left"}</p>
              <p className={`font-mono ${cert.expired ? "text-[#FF1744]" : (cert.days_to_expiry < 30 ? "text-[#FFB800]" : "text-[#00E676]")}`}>
                {cert.days_to_expiry != null ? cert.days_to_expiry : "—"}
              </p>
            </div>
          </div>
        )}
      </SectionCard>

      {/* CCTV/IoT — metadata only */}
      {host.cctv_iot && host.cctv_iot.length > 0 && (
        <SectionCard icon={Camera} color="#FFB800" title={lang === "it" ? "Dispositivi CCTV/IoT esposti" : "Exposed CCTV/IoT devices"} count={host.cctv_iot.length} testid="cyber-cctv-card">
          <div className="bg-[#FFB800]/6 px-3 py-2 border-b border-[#FFB800]/20 text-[10px] text-[#FFB800] flex items-center gap-2">
            <AlertOctagon className="w-3.5 h-3.5" />
            {lang === "it"
              ? "Solo metadati dispositivo — nessun feed video, screenshot o stream viene mostrato."
              : "Device metadata only — no video feed, screenshot or stream is displayed."}
          </div>
          <ul className="divide-y divide-white/[0.04]">
            {host.cctv_iot.map((d, i) => (
              <li key={i} className="px-3 py-2 flex items-center gap-3" data-testid={`cyber-cctv-${i}`}>
                <span className="text-xs font-mono text-[#FFB800] w-16">{d.port}/tcp</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{d.product} {d.version}</p>
                </div>
                {d.default_creds_hint && (
                  <span className="text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 bg-[#FF1744]/12 text-[#FF1744] border border-[#FF1744]/30 flex items-center gap-1" style={{ borderRadius: "2px" }} data-testid={`cyber-cctv-defcred-${i}`}>
                    <KeyRound className="w-3 h-3" /> {lang === "it" ? "Credenziali default" : "Default creds"}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
    </div>
  );
}

function AssetMap({ host }) {
  const { lang } = useLanguage();
  const { lat, lon, city, country_name } = host.geo || {};
  const critical = host.cyber_exposure_score.band === "critical" || host.cyber_exposure_score.band === "high_risk";
  const color = critical ? "#FF1744" : host.cyber_exposure_score.band === "medium_risk" ? "#FFB800" : "#00E676";
  return (
    <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }} data-testid="cyber-asset-map">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#00E5FF]" />
          <p className="text-[10px] tracking-widest uppercase font-bold text-[#00E5FF]">
            {lang === "it" ? "Mappa asset IT" : "IT asset map"}
          </p>
        </div>
        <span className="text-[10px] text-white">{city}, {country_name}</span>
      </div>
      <div style={{ height: "320px", width: "100%", background: "#050505" }}>
        <MapContainer
          center={[lat, lon]}
          zoom={5}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%", background: "#050505" }}
          zoomControl={true}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
            attribution='&copy; OSM &copy; CARTO'
          />
          {/* Pulse ring */}
          <CircleMarker
            center={[lat, lon]}
            radius={22}
            pathOptions={{ color: color, fillColor: color, fillOpacity: 0.08, opacity: 0.35, weight: 1 }}
          />
          <CircleMarker
            center={[lat, lon]}
            radius={7}
            pathOptions={{ color: color, fillColor: color, fillOpacity: 0.85, weight: 2 }}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={1}>
              <div style={{ fontFamily: "monospace", fontSize: "11px" }}>
                <b>{host.ip}</b><br />
                {host.organization}<br />
                <span style={{ color: "#FF1744" }}>Score: {host.cyber_exposure_score.score}/100</span>
              </div>
            </Tooltip>
          </CircleMarker>
        </MapContainer>
      </div>
    </div>
  );
}

function BreakdownChip({ label, value, penalty }) {
  return (
    <div className="bg-white/[0.02] border border-white/[0.04] px-2 py-1.5" style={{ borderRadius: "2px" }}>
      <p className="text-[9px] tracking-widest uppercase text-white truncate">{label}</p>
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-bold text-white tabular-nums">{value}</p>
        {penalty > 0 && <span className="text-[10px] text-[#FF1744] tabular-nums">-{penalty}</span>}
      </div>
    </div>
  );
}

function SectionCard({ icon: Icon, color, title, count, testid, children }) {
  return (
    <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }} data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} />
          <p className="text-[10px] tracking-widest uppercase font-bold" style={{ color }}>{title}</p>
        </div>
        {count !== "" && <span className="text-[10px] tabular-nums text-white">{count}</span>}
      </div>
      {children}
    </div>
  );
}
